import { useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Headroom from 'react-headroom';
import ThemeSwitcher from '@/components/ThemeSwitcher/index';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { SidebarNav } from '@/components/layout/SidebarNav';
import SidebarTrigger from '@/ui/SidebarTrigger';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import stadiumCover from '@/assets/images/world-cup-2026.webp';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

const ArrowIcon = () => (
  <svg className={styles.arrow} width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true">
    <path d="M1 6h12m0 0-5-5m5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LandingPage = () => {
  const { t } = useTranslation('landing');
  const headerRef = useRef<HTMLElement>(null);
  const isMobile = useBreakpointDown('lg');
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    document.title = 'PClash — Football Predictions with Friends';
  }, []);

  // Sticky header: frosted glass on scroll
  useEffect(() => {
    if (authLoading || isAuthenticated) return;
    const header = headerRef.current;
    if (!header) return;
    const onScroll = () => header.classList.toggle(styles.scrolled, window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [authLoading, isAuthenticated]);

  // Scroll-reveal animation
  useEffect(() => {
    if (authLoading || isAuthenticated) return;
    const els = document.querySelectorAll(`.${styles.reveal}`);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add(styles.revealIn); observer.unobserve(e.target); }
      }),
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [authLoading, isAuthenticated]);

  // Authenticated users: redirect to dashboard without rendering landing page
  if (authLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <div className={styles.landingPage}>
      <SidebarNav />
      <a className={styles.skipLink} href="#main">Skip to content</a>

      {/* ===== HEADER ===== */}
      <Headroom style={{ transition: 'all .3s ease-in-out', zIndex: 100 }}>
        <header className={styles.siteHeader} ref={headerRef}>
          <div className={`${styles.container} ${styles.headerInner}`}>
            <Link className={styles.logo} to="/" aria-label="PClash home">
              <span className={styles.logoPlay}><span>PClash</span></span>
            </Link>
            <nav className={styles.nav} aria-label="Primary">
              <a href="#how">{t('navHowItWorks')}</a>
              <a href="#leagues">{t('navLeagues')}</a>
              <a href="#scoring">{t('navScoring')}</a>
              <a href="#faq">{t('navFaq')}</a>
            </nav>
            <div className={styles.headerActions}>
              <LanguageSwitcher />
              <ThemeSwitcher />
              {!isMobile && (
                <>
                  <Link className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`} to="/login">{t('navSignIn')}</Link>
                  <Link className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`} to="/sign-up">{t('startPredicting')}</Link>
                </>
              )}
              {isMobile && (
                <div className={styles.burgerWrap}>
                  <SidebarTrigger />
                </div>
              )}
            </div>
          </div>
        </header>
      </Headroom>

      <main id="main">
        {/* ===== HERO ===== */}
        <section className={styles.hero} aria-labelledby="hero-title">
          <div className={styles.heroPitch} aria-hidden="true" />
          <div className={styles.container}>
            <div className={styles.heroGrid}>
              <div>
                <span className={styles.eyebrow}>Season 2025/26</span>
                <h1 id="hero-title" className={styles.heroTitle} dangerouslySetInnerHTML={{ __html: t('heroTitle') }} />
                <p className={styles.heroLede}>
                  PClash is a football prediction clash. Create a private tournament for your friends, or join a public one across Europe&#39;s top leagues — we score every pick the moment the match ends.
                </p>
                <div className={styles.ctaRow}>
                  <Link className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`} to="/sign-up">Start Predicting <ArrowIcon /></Link>
                  <a className={`${styles.btn} ${styles.btnGhost} ${styles.btnLg}`} href="#how">How it works</a>
                </div>
                <div className={styles.proof} aria-label="Platform stats">
                  <div className={styles.proofItem}><span className={styles.proofValue}>12,431</span><span className={styles.proofLabel}>Active tournaments</span></div>
                  <div className={styles.proofSep} aria-hidden="true" />
                  <div className={styles.proofItem}><span className={styles.proofValue}>84K+</span><span className={styles.proofLabel}>Predictions weekly</span></div>
                  <div className={styles.proofSep} aria-hidden="true" />
                  <div className={styles.proofItem}><span className={styles.proofValue}>14</span><span className={styles.proofLabel}>Leagues covered</span></div>
                </div>
              </div>

              {/* Hero Art — decorative match card stack */}
              <div className={styles.heroArt} aria-hidden="true">
                {/* Card A: upcoming */}
                <div className={`${styles.matchCard} ${styles.cardA}`}>
                  <div className={styles.mcHead}><span className={styles.mcLeague}>Premier League</span><span className={`${styles.chipStatus} ${styles.chipScheduled}`}><span className={styles.chipDot} />Sat 21:00</span></div>
                  <div className={styles.mcTeams}>
                    <div className={styles.mcTeam}><div className={styles.crest}>A</div><div className={styles.mcTeamName}>Arsenal</div></div>
                    <div className={styles.mcScore}><div className={styles.scoreBox}>2</div><div className={styles.scoreDash}>–</div><div className={styles.scoreBox}>1</div></div>
                    <div className={styles.mcTeam}><div className={styles.crest}>C</div><div className={styles.mcTeamName}>Chelsea</div></div>
                  </div>
                  <div className={styles.mcPicks}>
                    <div className={`${styles.pick} ${styles.pickSelected}`}><span className={styles.pickLabel}>Home (1)</span><span className={styles.pickOdd}>1.85</span></div>
                    <div className={styles.pick}><span className={styles.pickLabel}>Draw (X)</span><span className={styles.pickOdd}>3.40</span></div>
                    <div className={styles.pick}><span className={styles.pickLabel}>Away (2)</span><span className={styles.pickOdd}>4.20</span></div>
                  </div>
                </div>
                {/* Card B: live */}
                <div className={`${styles.matchCard} ${styles.cardB}`}>
                  <div className={styles.mcHead}><span className={styles.mcLeague}>Champions League</span><span className={`${styles.chipStatus} ${styles.chipLive}`}><span className={styles.chipDot} />Live 67&apos;</span></div>
                  <div className={styles.mcTeams}>
                    <div className={styles.mcTeam}><div className={styles.crest}>R</div><div className={styles.mcTeamName}>Real Madrid</div></div>
                    <div className={styles.mcScore}><div className={`${styles.scoreBox} ${styles.scoreBoxActive}`}>3</div><div className={styles.scoreDash}>–</div><div className={`${styles.scoreBox} ${styles.scoreBoxActive}`}>2</div></div>
                    <div className={styles.mcTeam}><div className={styles.crest}>B</div><div className={styles.mcTeamName}>Bayern</div></div>
                  </div>
                  <div className={styles.mcAction}><span className={styles.mcActionKey}>Your pick</span><span className={styles.mcActionValue}>3 – 2 · Home Win</span></div>
                </div>
                {/* Card C: finished */}
                <div className={`${styles.matchCard} ${styles.cardC}`}>
                  <div className={styles.mcHead}><span className={styles.mcLeague}>La Liga</span><span className={`${styles.chipStatus} ${styles.chipFinished}`}><span className={styles.chipDot} />Finished</span></div>
                  <div className={styles.mcTeams}>
                    <div className={styles.mcTeam}><div className={styles.crest}>B</div><div className={styles.mcTeamName}>Barcelona</div></div>
                    <div className={styles.mcScore}><div className={styles.scoreBox}>2</div><div className={styles.scoreDash}>–</div><div className={styles.scoreBox}>0</div></div>
                    <div className={styles.mcTeam}><div className={styles.crest}>V</div><div className={styles.mcTeamName}>Valencia</div></div>
                  </div>
                  <div className={styles.mcAction}><span className={styles.mcActionKey}>You scored</span><span className={styles.mcActionValue} style={{ color: 'var(--green)' }}>+12 PTS</span></div>
                </div>
                {/* Leaderboard mini */}
                <aside className={styles.leaderboardMini}>
                  <h6>{t('fridayNightClash')}</h6>
                  <ol>
                    <li><span className={styles.lbRank}>01</span><span><span className={styles.lbDot} style={{ background: 'var(--red)' }} />marco.d</span><span className={styles.lbPts}>148</span></li>
                    <li className={styles.lbYou}><span className={styles.lbRank}>02</span><span><span className={styles.lbDot} style={{ background: 'var(--blue)' }} />you</span><span className={styles.lbPts}>142</span></li>
                    <li><span className={styles.lbRank}>03</span><span><span className={styles.lbDot} style={{ background: 'var(--grass)' }} />sasha</span><span className={styles.lbPts}>129</span></li>
                    <li><span className={styles.lbRank}>04</span><span><span className={styles.lbDot} style={{ background: 'var(--purple)' }} />lenny</span><span className={styles.lbPts}>118</span></li>
                  </ol>
                </aside>
              </div>
            </div>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className={styles.hiw} id="how" aria-labelledby="how-title">
          <div className={styles.container}>
            <div className={`${styles.sectionHead} ${styles.reveal}`}>
              <span className={styles.eyebrow}>{t('navHowItWorks')}</span>
              <h2 id="how-title">{t('howItWorksTitle')}</h2>
              <p>{t('howItWorksSubtitle')}</p>
            </div>
            <div className={styles.hiwSteps}>
              <article className={`${styles.step} ${styles.reveal}`}>
                <span className={styles.stepNum}>01</span>
                <h3>{t('step1Title')}</h3>
                <p>{t('step1Desc')}</p>
                <div className={styles.stepVisual}>
                  <div className={styles.tournamentChips}>
                    <div className={`${styles.tChip} ${styles.tChipActive}`}>Friday Night Clash<span className={styles.tChipCount}>12 Friends</span></div>
                    <div className={styles.tChip}>Office League<span className={styles.tChipCount}>28 Players</span></div>
                    <div className={styles.tChip}>Champions Public<span className={styles.tChipCount}>8,412 Players</span></div>
                  </div>
                </div>
              </article>
              <article className={`${styles.step} ${styles.reveal}`}>
                <span className={styles.stepNum}>02</span>
                <h3>{t('step2Title')}</h3>
                <p>{t('step2Desc')}</p>
                <div className={styles.stepVisual}>
                  <div className={styles.picksMini}>
                    <div className={`${styles.sbox} ${styles.sboxFill}`}>2</div>
                    <div className={styles.sboxDash}>–</div>
                    <div className={`${styles.sbox} ${styles.sboxFill}`}>1</div>
                    <div style={{ width: 12 }} />
                    <div className={styles.sbox} style={{ flex: '0 0 auto', width: 52, fontSize: 11, color: 'var(--header-light)', background: 'var(--accent)', borderColor: 'var(--accent)' }}>1</div>
                  </div>
                </div>
              </article>
              <article className={`${styles.step} ${styles.reveal}`}>
                <span className={styles.stepNum}>03</span>
                <h3>{t('step3Title')}</h3>
                <p>{t('step3Desc')}</p>
                <div className={styles.stepVisual}>
                  <div className={styles.lbMini}>
                    <div className={styles.lbMiniRow}><span className={styles.lbMiniRank}>01</span><span>marco.d</span><span className={styles.lbMiniPts}>148</span></div>
                    <div className={`${styles.lbMiniRow} ${styles.lbMiniMe}`}><span className={styles.lbMiniRank}>02</span><span>you</span><span className={styles.lbMiniPts}>142</span></div>
                    <div className={styles.lbMiniRow}><span className={styles.lbMiniRank}>03</span><span>sasha</span><span className={styles.lbMiniPts}>129</span></div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* ===== LEAGUES ===== */}
        <section className={styles.leaguesSection} id="leagues" aria-labelledby="leagues-title">
          <div className={styles.container}>
            <div className={`${styles.sectionHead} ${styles.reveal}`}>
              <span className={styles.eyebrow}>Supported competitions</span>
              <h2 id="leagues-title">{t('leaguesTitle')}</h2>
              <p>{t('leaguesSubtitle')}</p>
            </div>
            <div className={styles.leaguesGrid}>
              {[
                { flag: '🏆', name: 'Champions League', meta: '32 teams · Group Stage', active: true },
                { flag: '⚽', name: 'Premier League', meta: '20 teams · Matchday 24', active: true },
                { flag: '🇪🇸', name: 'La Liga', meta: '20 teams · Matchday 22', active: true },
                { flag: '🇮🇹', name: 'Serie A', meta: '20 teams · Matchday 23', active: true },
                { flag: '🇩🇪', name: 'Bundesliga', meta: '18 teams · Matchday 21', active: true },
                { flag: '🇫🇷', name: 'Ligue 1', meta: '18 teams · Matchday 22', active: true },
                { flag: '🛡️', name: 'Europa League', meta: '32 teams · Knockouts', active: false },
                { flag: '🌍', name: 'World Cup 2026', meta: '48 teams · Qualifiers', active: false },
              ].map((league) => (
                <article key={league.name} className={`${styles.leagueCard} ${styles.reveal}`}>
                  <div className={styles.lcFlag} aria-hidden="true">{league.flag}</div>
                  <div className={styles.lcName}>{league.name}</div>
                  <div className={styles.lcMeta}>{league.meta}</div>
                  <span className={`${styles.lcTag} ${!league.active ? styles.lcTagUpcoming : ''}`}>{league.active ? 'Active' : 'Upcoming'}</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SCORING ===== */}
        <section className={styles.scoring} id="scoring" aria-labelledby="scoring-title">
          <div className={styles.container}>
            <div className={styles.scoringGrid}>
              <div className={styles.reveal}>
                <span className={styles.eyebrow}>Scoring system</span>
                <h2 id="scoring-title">{t('scoringTitle')}</h2>
                <p dangerouslySetInnerHTML={{ __html: t('scoringSubtitle') }} />
                <ul className={styles.scoringFeatures}>
                  {[
                    ['Exact score hit.', 'Nail the final score to claim the highest-coefficient reward.'],
                    ['Result-only hit.', 'Got the winner right but missed the score? You still score on the result coefficient.'],
                    ['Instant settlement.', 'Points post to the leaderboard the moment the full-time whistle blows.'],
                    ['No negatives.', 'Missed picks are a zero — never a deduction. Just keep playing.'],
                  ].map(([title, desc]) => (
                    <li key={title}>
                      <span className={styles.sfCheck} aria-hidden="true">✓</span>
                      <span className={styles.sfText}><strong>{title}</strong> {desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`${styles.breakdown} ${styles.reveal}`} aria-label="Example scoring breakdown">
                <div className={styles.bdHeader}><span className={styles.bdLeague}>Matchday 24 · Premier League</span><span className={styles.bdResult}>Full-time</span></div>
                <div className={styles.matchline}><div className={styles.mlName}>Arsenal</div><div className={styles.mlFinal}>2 – 1</div><div className={`${styles.mlName} ${styles.mlAway}`}>Chelsea</div></div>
                <div className={styles.bdRows}>
                  <div className={styles.bdRow}><span>Exact score (2–1)</span><span className={styles.bdCoef}>×4.20</span><span className={styles.bdPts}>+8</span></div>
                  <div className={styles.bdRow}><span>Match result (Home)</span><span className={styles.bdCoef}>×1.85</span><span className={styles.bdPts}>+4</span></div>
                  <div className={`${styles.bdRow} ${styles.bdRowTotal}`}><span className={styles.bdTotalLabel}>Your haul</span><span className={styles.bdCoef} /><span className={styles.bdPts}>+12 PTS</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== TELEGRAM ===== */}
        <section className={styles.tg} aria-labelledby="tg-title">
          <div className={styles.container}>
            <div className={`${styles.tgCard} ${styles.reveal}`}>
              <div>
                <span className={styles.eyebrow}>Telegram Mini App</span>
                <h2 id="tg-title">{t('telegramTitle')}</h2>
                <p>{t('telegramSubtitle')}</p>
                <div className={styles.tgCtas}>
                  <a className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`} href="http://tbd.loc" rel="noopener noreferrer">{t('openInTelegram')} <ArrowIcon /></a>
                  <Link className={`${styles.btn} ${styles.btnGhost} ${styles.btnLg}`} to="/sign-up">Or sign up on web</Link>
                </div>
              </div>
              <div className={styles.tgPhone} aria-hidden="true">
                <div className={styles.tgNotch} />
                <div className={styles.tgScreen}>
                  <div className={styles.tgHead}><div className={styles.tgAvatar}>FN</div><div><div className={styles.tgName}>Friday Night Clash</div><div className={styles.tgStatus}>12 friends · 4 fixtures</div></div></div>
                  <div className={styles.tgMiniMatch}>
                    <div className={styles.tgMiniLabel}>Premier League · Sat 21:00</div>
                    <div className={styles.tgMiniTeams}><span>Arsenal</span><span><span className={`${styles.tgMiniScore} ${styles.tgMiniScoreHl}`}>2</span> – <span className={`${styles.tgMiniScore} ${styles.tgMiniScoreHl}`}>1</span></span><span>Chelsea</span></div>
                    <div className={styles.tgMiniPicks}><div className={styles.tgMiniPickSel}>1</div><div>X</div><div>2</div></div>
                  </div>
                  <div className={styles.tgMiniMatch}>
                    <div className={styles.tgMiniLabel}>Champions League · Live 67&apos;</div>
                    <div className={styles.tgMiniTeams}><span>Real</span><span><span className={styles.tgMiniScore}>3</span> – <span className={styles.tgMiniScore}>2</span></span><span>Bayern</span></div>
                    <div className={styles.tgMiniPicks}><div className={styles.tgMiniPickSel}>1</div><div>X</div><div>2</div></div>
                  </div>
                  <div className={styles.tgMiniMatch}>
                    <div className={styles.tgMiniLabel}>La Liga · Sun 18:30</div>
                    <div className={styles.tgMiniTeams}><span>Barça</span><span><span className={styles.tgMiniScore}>—</span> – <span className={styles.tgMiniScore}>—</span></span><span>Atleti</span></div>
                    <div className={styles.tgMiniPicks}><div>1</div><div>X</div><div>2</div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className={styles.faq} id="faq" aria-labelledby="faq-title">
          <div className={styles.container}>
            <div className={`${styles.sectionHead} ${styles.reveal}`}>
              <span className={styles.eyebrow}>Questions</span>
              <h2 id="faq-title">{t('faqTitle')}</h2>
            </div>
            <div className={styles.faqList}>
              {[
                { q: 'What is PClash?', a: 'PClash is a football prediction app. You create private tournaments from existing leagues to compete with friends, or join public tournaments open to everyone. For each fixture you set a score prediction and a match-outcome pick; points are awarded when the match ends.', open: true },
                { q: 'How is scoring calculated?', a: 'Every prediction is scored against two coefficients: the exact-score coefficient and the match-result coefficient. You earn points for getting the winner right, and extra points for nailing the exact score. Underdog picks carry higher multipliers.' },
                { q: 'Is PClash free to use?', a: "Yes — at launch, everything is free. You can create an account, join public tournaments, and run private tournaments with friends at no cost. Down the road we plan to introduce paid private tournaments and competitions; pricing hasn't been set yet, and anything you start today stays free." },
                { q: 'Which leagues are supported?', a: "PClash covers Europe's top competitions — the UEFA Champions League, Premier League, La Liga, Serie A, Bundesliga, Ligue 1, Europa League, and more domestic cups. New competitions are added each season." },
                { q: 'Can I play on Telegram?', a: 'Yes. PClash ships as a native Telegram Mini App, so you can predict fixtures and check leaderboards without leaving the group chat where your friends already hang out.' },
                { q: 'Can I edit a prediction after submitting?', a: 'Yes — any prediction can be edited up to kickoff. Once the match starts, your pick is locked.' },
              ].map((item) => (
                <details key={item.q} className={`${styles.faqItem} ${styles.reveal}`} open={item.open}>
                  <summary>{item.q}</summary>
                  <div className={styles.faqAnswer}>{item.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className={styles.finalCta} aria-labelledby="cta-title" style={{ '--stadium-bg': `url(${stadiumCover})` } as React.CSSProperties}>
          <div className={styles.container}>
            <span className={styles.eyebrow}>Season 2025/26</span>
            <h2 id="cta-title" dangerouslySetInnerHTML={{ __html: t('ctaTitle') }} />
            <p>{t('ctaSubtitle')}</p>
            <div className={styles.ctaRow}>
              <Link className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`} to="/sign-up">Start Predicting <ArrowIcon /></Link>
              <Link className={`${styles.btn} ${styles.btnGhost} ${styles.btnLg}`} to="/login">I already have an account</Link>
            </div>
            <div className={styles.ctaMicro}>{t('heroSubtitle')}</div>
          </div>
        </section>
      </main>

      {/* ===== FOOTER ===== */}
      <footer className={styles.siteFooter}>
        <div className={styles.container}>
          <div className={styles.footerTop}>
            <div className={styles.footerBrand}>
              <Link className={styles.logo} to="/" aria-label="PClash home"><span className={styles.logoPlay}><span>PClash</span></span></Link>
              <p>{t('heroFooterTitle')}</p>
            </div>
            <div className={styles.footerCol}>
              <h5>{t('navProduct')}</h5>
              <ul><li><a href="#how">{t('navHowItWorks')}</a></li><li><a href="#leagues">{t('navLeagues')}</a></li><li><a href="#scoring">{t('navScoring')}</a></li><li><a href="http://tbd.loc" rel="noopener noreferrer">Telegram Mini App</a></li></ul>
            </div>
            <div className={styles.footerCol}>
              <h5>Company</h5>
              <ul><li><a href="/about">About</a></li><li><a href="/blog">Blog</a></li><li><a href="/careers">Careers</a></li><li><a href="/contact">Contact</a></li></ul>
            </div>
            <div className={styles.footerCol}>
              <h5>Legal</h5>
              <ul><li><a href="/privacy">Privacy Policy</a></li><li><a href="/terms">Terms of Service</a></li><li><a href="/cookies">Cookie Policy</a></li><li><a href="/responsible-play">Responsible Play</a></li></ul>
            </div>
          </div>
          <div className={styles.footerBottom}>
            <span className={styles.footerCopy}>© 2026 PClash. All rights reserved.</span>
            <span className={styles.footerAge}><span className={styles.footerBadge}>18+</span>Play responsibly</span>
            <div className={styles.footerSocials}>
              <a href="http://tbd.loc" aria-label="Telegram" rel="noopener noreferrer"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" /></svg></a>
              <a href="http://tbd.loc" aria-label="Twitter" rel="noopener noreferrer"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg></a>
              <a href="http://tbd.loc" aria-label="Instagram" rel="noopener noreferrer"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" /></svg></a>
              <a href="http://tbd.loc" aria-label="YouTube" rel="noopener noreferrer"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.5a3 3 0 0 0-2.11-2.12C19.5 4 12 4 12 4s-7.5 0-9.39.38A3 3 0 0 0 .5 6.5 31.3 31.3 0 0 0 .12 12a31.3 31.3 0 0 0 .38 5.5 3 3 0 0 0 2.11 2.12C4.5 20 12 20 12 20s7.5 0 9.39-.38a3 3 0 0 0 2.11-2.12A31.3 31.3 0 0 0 23.88 12a31.3 31.3 0 0 0-.38-5.5zM9.75 15.5v-7l6.5 3.5z" /></svg></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
